import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../layout/Container';
import { scrollToSection } from '../../utils/scrollUtils';

interface Project {
  id: string;
  title: string;
  description: string;
  detailedDescription?: string; // Detailed description for modal
  tags: string[];
  image?: string;
  link?: string;
  github?: string;
  isLogo?: boolean; // Adding a prop to identify logo images
  extraSpace?: boolean; // Add extra space below short titles
}

// Custom component to handle different types of source links
const ViewSourceLink = ({ project }: { project: Project }) => {
  // Don't show View Project for GameUp, GoLocal, and Nidra
  if (project.id === 'gameup' || project.id === 'golocal' || project.id === 'nidra') {
    return null;
  }

  // For the portfolio website, scroll to top
  if (project.id === 'personal-website') {
    return (
      <ProjectLink onClick={() => scrollToSection('home')}>
        View Project
      </ProjectLink>
    );
  }
  
  // For the stock-analysis project, link to the dedicated page
  if (project.id === 'stock-analysis') {
    return (
      <ProjectLink as={Link} to="/stock-analysis">
        View Project
      </ProjectLink>
    );
  }
  
  // For the ai-hacker project, link to the cyberdefense page
  if (project.id === 'ai-hacker') {
    return (
      <ProjectLink as={Link} to="/cyberdefense">
        View Project
      </ProjectLink>
    );
  }
  
  // For other projects with GitHub links
  if (project.github) {
    return (
      <ProjectLink href={project.github} target="_blank" rel="noopener noreferrer">
        View Project
      </ProjectLink>
    );
  }
  
  return null;
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  
  const personalProjects: Project[] = [
    {
      id: 'stock-analysis',
      title: 'AI Stock Analysis Platform',
      description: 'A comprehensive stock market analysis platform with automated data collection, financial modeling, and AI-driven analysis capabilities. This hobby project evolved from my interest in combining financial expertise with technical skills.',
      tags: ['Python', 'Django', 'React', 'Docker', 'Financial API'],
      image: '/images/stock-analysis-logo.svg',
      github: 'https://github.com/christopheranto/stock-analysis',
      isLogo: true, // Mark this image as a logo
    },
    {
      id: 'personal-website',
      title: 'Personal Portfolio Website',
      description: 'A modern, responsive portfolio website built with React and TypeScript. Features interactive animations, optimized performance,and a modular component architecture. Deployed using Docker and Nginx for robust, production-ready hosting.',
      tags: ['React', 'TypeScript', 'Docker', 'Styled Components', 'Framer Motion'],
      image: '/images/portfolio-logo.svg',
      github: 'https://github.com/christopheranto/christopher_website',
      isLogo: true, // Mark this image as a logo
    },
    {
      id: 'ai-hacker',
      title: 'AI Cyberdefense System',
      description: 'An advanced AI-driven security system that identifies vulnerabilities through automated intelligence processing and machine learning. This personal project reflects my interest in the intersection of AI and cybersecurity.',
      tags: ['Python', 'Docker', 'Machine Learning', 'NLP', 'Security'],
      image: '/images/ai-security-logo.svg',
      github: 'https://github.com/christopheranto/ai-hacker',
      isLogo: true, // Mark this image as a logo
    },
  ];

  const professionalProjects: Project[] = [
    {
      id: 'gameup',
      title: 'GameUP',
      description: 'GameUP is the flagship product of Integrity-IT Technologies—a sports talent platform spanning ten disciplines where grassroots athletes discover tournaments and showcase ability. I defined the product vision, ran 50+ user interviews and a VIT Chennai pilot, and grew the app to 10,000+ organic downloads in eight months with zero marketing spend, leading a team of eight across engineering, design, and QA.',
      detailedDescription: `GameUP is a tournament and talent platform for grassroots athletes across India. As founder and senior product manager, I shaped the vision, roadmap, and growth strategy—and built the product with a cross-functional team.

**The Vision:**
GameUP closes the gap in local sports communities, helping athletes find, register for, and compete in tournaments across ten disciplines including cricket, football, basketball, and badminton.

**Key Achievements:**
- 10,000+ organic downloads in eight months with no marketing budget
- 50+ user interviews informing product direction
- Successful pilot tournament programme at VIT Chennai
- Cross-functional team of eight across engineering, design, and QA

**Product Development:**
I led discovery, roadmap prioritisation, and release planning—from early market research through App Store publishing. The VIT Chennai pilot and ongoing user feedback directly shaped feature iterations and product-market fit.

**Technical Leadership:**
I stayed hands-on on critical engineering decisions, kept the product stable across platforms, and made sure the architecture could support organic growth as usage scaled.

**Business Impact:**
Zero marketing spend forced a focus on product quality and word-of-mouth growth—resulting in sustainable adoption and strong validation that the platform solved a real problem for local sports communities.`,
      tags: ['User Research & Validation', 'Team Leadership', 'Organic Growth Strategy', 'Product Vision & Strategy', 'Sports Tech'],
      image: '/images/gameup-logo.png',
      link: 'https://gameup.app',
      isLogo: true, // Mark this image as a logo
    },
    {
      id: 'golocal',
      title: 'GoLocal',
      description: 'GoLocal is a hyperlocal grocery delivery platform with a zero-commission model and elderly-first design. I built it entirely solo—from product concept and branding through full-stack architecture on React Native, FastAPI, PostgreSQL, and AWS—to App Store approval, with major gains in infrastructure efficiency.',
      detailedDescription: `GoLocal is a hyperlocal grocery delivery platform I built solo—from first idea and brand identity through App Store approval.

**Product Positioning:**
A zero-commission alternative to major delivery apps, designed first for elderly users who need large touch targets, clear contrast, and simple flows.

**End-to-End Ownership:**
I handled concept, logo, website, mobile app, backend, infrastructure, testing, and store submission without a team—full product and engineering ownership in one build.

**Infrastructure Efficiency:**
Systematic optimisation cut running costs sharply while improving performance:
- 95% lower SMS spend by moving from Firebase Phone Auth to AWS SNS with Redis
- 99.9% less data transfer for store inventory through layered caching
- 40% lower AWS bills via caching, indexing, and architecture tuning

**Stack & Delivery:**
React Native and Expo on mobile, FastAPI and PostgreSQL on the backend, AWS for hosting and services, plus a Next.js admin console. Shipped to both iOS and Android app stores.`,
      tags: ['Full-Stack Architecture', 'Cross-Platform Deployment', 'Infrastructure Optimization', 'Production DevOps', 'End-to-End Ownership'],
      image: '/images/golocal-logo.png',
      link: 'https://golocalnow.in',
      isLogo: true, // Mark this image as a logo
    },
    {
      id: 'nidra',
      title: 'Nidra',
      description: 'Freelance product and engineering lead for Nidra N3 Limited (UK)—a sleep wellness platform now in pilot testing. I built the mobile app, backend, payments, device tracking, admin tools, and nidra.io, integrating client-provided EEG headband hardware and a patented sleep algorithm into one cohesive product in under three months.',
      detailedDescription: `Nidra N3 Limited is a UK sleep wellness startup—not a product I founded. I joined as the sole product manager and full-stack engineer to build their platform from the ground up.

**My Role:**
I owned roadmap, sprint planning with the UK founding team, and over 15 user research sessions—alongside all engineering delivery. The product vision and core science belong to the client; I turned requirements into a shippable system.

**Headband Integration:**
I connected the mobile app to the client's EEG headband over Bluetooth so patients can pair the device, record brainwaves, and upload data without manual steps—the hardware and patented algorithm were provided; I made them work together inside the app.

**From Build to Pilot:**
I took the platform from concept to live pilot in under three months—mobile app, FastAPI and PostgreSQL backend, AWS infrastructure, and store submission readiness.

**Payments & Fulfilment:**
Stripe checkout for consultations and device purchases, real-time order tracking, push notifications, and a web admin dashboard for the operations team.

**What I Built:**
- Patient and doctor mobile app: headband recording, personalised sleep sounds, habit tracking, questionnaires, payments, and notifications
- Cloud backend: accounts, appointments, recording pipeline, payments, and secure storage
- Admin dashboard and doctor consultation web experience
- Public marketing site and onboarding flow at nidra.io

**Pilot Testing:**
The product is in pilot testing with early users across the UK—not yet publicly launched. Backend and admin tooling are live and ready to scale after the pilot.

**What I Delivered:**
A pilot-ready sleep wellness platform—integrated headband workflow, mobile app, payments, fulfilment, admin tools, and public website—as freelance product and engineering lead for Nidra N3 Limited.`,
      tags: ['Full-Stack Development', 'Product Management', 'Healthtech', 'Hardware Integration', 'Rapid Delivery'],
      image: '/images/nidra-logo.png',
      link: 'https://nidra.io',
      isLogo: true,
    },
  ];

  return (
    <ProjectsSection id="projects">
      <Container>
        <SectionTitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            My Projects
          </motion.div>
        </SectionTitle>

        <ProjectsSubtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Professional Projects
          </motion.div>
        </ProjectsSubtitle>

        <ProjectsGrid>
          {professionalProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
            >
              <ProjectImageContainer isLogo={project.isLogo}>
                {project.image ? (
                  <ProjectImage 
                    src={project.image} 
                    alt={project.title}
                    isLogo={project.isLogo}
                    isGoLocal={project.id === 'golocal'}
                  />
                ) : (
                  <ProjectImagePlaceholder />
                )}
              </ProjectImageContainer>
              <ProjectContent>
                <ProjectTitle centered={project.isLogo} extraSpace={project.extraSpace}>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ViewMoreContainer>
                  {(project.id === 'gameup' || project.id === 'golocal' || project.id === 'nidra') && project.detailedDescription && (
                    <ViewMoreButton onClick={() => setSelectedProject(project)}>
                      View More...
                    </ViewMoreButton>
                  )}
                </ViewMoreContainer>
                <TagsContainer>
                  {project.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </TagsContainer>
                <ProjectLinks>
                  {project.link && (
                    <ProjectLink href={project.link} target="_blank" rel="noopener noreferrer">
                      Visit Project
                    </ProjectLink>
                  )}
                  <ViewSourceLink project={project} />
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>

        <ProjectsSubtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Personal Projects
          </motion.div>
        </ProjectsSubtitle>

        <ProjectsGrid>
          {personalProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
            >
              <ProjectImageContainer isLogo={project.isLogo}>
                {project.image ? (
                  <ProjectImage 
                    src={project.image} 
                    alt={project.title}
                    isLogo={project.isLogo}
                    isGoLocal={project.id === 'golocal'}
                  />
                ) : (
                  <ProjectImagePlaceholder />
                )}
              </ProjectImageContainer>
              <ProjectContent>
                <ProjectTitle centered={project.isLogo} extraSpace={project.extraSpace}>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <TagsContainer>
                  {project.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </TagsContainer>
                <ProjectLinks>
                  {project.link && (
                    <ProjectLink href={project.link} target="_blank" rel="noopener noreferrer">
                      Visit Project
                    </ProjectLink>
                  )}
                  <ViewSourceLink project={project} />
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </ProjectsSection>
  );
};

// Modal Component
const ProjectModal = ({ project, onClose }: { project: Project; onClose: () => void }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <ModalOverlay
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        as={motion.div}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>{project.title}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          {project.detailedDescription?.split('\n\n').map((paragraph, index) => {
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              const text = paragraph.replace(/\*\*/g, '');
              return <ModalSubtitle key={index}>{text}</ModalSubtitle>;
            }
            if (paragraph.startsWith('**') && paragraph.includes(':**')) {
              const parts = paragraph.split(':**');
              const title = parts[0].replace(/\*\*/g, '');
              const content = parts[1]?.trim();
              return (
                <div key={index}>
                  <ModalSubtitle>{title}:</ModalSubtitle>
                  {content && <ModalText>{content}</ModalText>}
                </div>
              );
            }
            if (paragraph.startsWith('- ')) {
              return <ModalListItem key={index}>{paragraph.replace(/^-\s*/, '')}</ModalListItem>;
            }
            return <ModalText key={index}>{paragraph}</ModalText>;
          })}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const ProjectsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  padding-top: ${({ theme }) => theme.spacing.lg}; /* Significantly reduced padding at top to bring section closer to navbar */
  background-color: ${({ theme }) => theme.colors.background};
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(to right, #FFFFFF, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const ProjectsSubtitle = styled.h3`
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.normal}, 
              box-shadow ${({ theme }) => theme.transitions.normal};
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.boxShadow.elevated};
  }
`;

const ProjectImageContainer = styled.div<{ isLogo?: boolean }>`
  height: 200px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  padding: ${({ isLogo }) => isLogo ? '30px 30px' : '0'};
`;

const ProjectImage = styled.img<{ isLogo?: boolean; isGoLocal?: boolean }>`
  width: ${({ isLogo, isGoLocal }) => {
    if (isGoLocal) return '42.3%'; // 55% / 1.3 to maintain same visible size after 30% zoom
    return isLogo ? '55%' : '100%';
  }};
  height: ${({ isLogo }) => isLogo ? 'auto' : '100%'};
  max-height: ${({ isLogo, isGoLocal }) => {
    if (isGoLocal) return '61.5%'; // 80% / 1.3 to maintain same visible size after 30% zoom
    return isLogo ? '80%' : '100%';
  }};
  object-fit: ${({ isLogo }) => isLogo ? 'contain' : 'cover'};
  transition: transform ${({ theme }) => theme.transitions.normal};
  transform: ${({ isGoLocal }) => isGoLocal ? 'scale(1.3)' : 'scale(1)'};
  object-position: center;
  
  ${ProjectCard}:hover & {
    transform: scale(${({ isLogo, isGoLocal }) => {
      if (isGoLocal) return '1.4';
      return isLogo ? '1.1' : '1.05';
    }});
  }
`;

const ProjectImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  opacity: 0.6;
`;

const ProjectContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ProjectTitle = styled.h4<{ centered?: boolean; extraSpace?: boolean }>`
  margin-bottom: ${({ theme, extraSpace }) => extraSpace ? theme.spacing.lg : theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  text-align: ${({ centered }) => centered ? 'center' : 'left'};
  font-size: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: 0.95rem;
  line-height: 1.6;
  min-height: 6.5em;
`;

const ViewMoreContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  margin-top: auto;
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Tag = styled.span`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: rgba(10, 132, 255, 0.1);
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 0.75rem;
  font-weight: 500;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: 0;
`;

const ProjectLink = styled.a`
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Modal Styled Components
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
  backdrop-filter: blur(4px);
`;

const ModalContent = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.medium || '16px'};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.boxShadow.elevated || '0 20px 60px rgba(0, 0, 0, 0.5)'};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor || 'rgba(255, 255, 255, 0.1)'};
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  background: linear-gradient(to right, #FFFFFF, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 2.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background || '#1a1a1a'};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary || '#4A90E2'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.secondary || '#22c55e'};
  }
`;

const ModalText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalSubtitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:first-child {
    margin-top: 0;
  }
`;

const ModalListItem = styled.li`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  margin-left: ${({ theme }) => theme.spacing.md};
  
  &::marker {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ViewMoreButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  padding: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease;
  text-decoration: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export default Projects;
